import FlexContainer from '../../../common/FlexContainer';
import {FormResults} from '../../../common/Form';
import Spinner from '../../../common/loading/Spinner';
import Typography from '../../../common/text/Typography';
import {useOperateAttrMutation} from '../../api';
import {mergeErrorStates, extractQueryErrMessage} from '../../api/helpers';
import {BaseAttribute} from '../../ontology/types/attributeTypes';
import {AttrActionFormProps} from '../common/commonTypes';
import {
    renderReferenceField,
    renderDerivationSubfields,
} from '../common/jsxHelpers';
import useAttrNeighbors from '../hooks/useAttrNeighbors';
import {
    prependAttrFormDefaults,
    prependAttrFormToPayload,
} from './prependAttributeHelpers';
import {PrependAttrFormValues} from './prependAttributeTypes';
import React, {FunctionComponent, useCallback, useEffect} from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import AppModal from "../../../common/modals/AppModal";
import ValueTypeSelect from "../../../common/inputs/ValueTypeSelect";
import IsDerivationInput from "../../../common/inputs/IsDerivationInput";
import DelimiterTypeSelect from "../../../common/inputs/DelimiterTypeSelect";
import useModalType from "../../HUD/hooks/useModalType";
import {setModalType} from "../../HUD/state/HUDSlice";
import Loading from "../../../common/loading/Loading";
import Uninitialized from "../../../common/loading/Uninitialized";


const PrependAttributeForm: FunctionComponent = () => {

    // const {_id, updatePopper, canEdit} = props;
    const {modalProps, closeModal} = useModalType();
    const {objectId: _id, canEdit} = modalProps;

    const formDefaults = prependAttrFormDefaults();

    const {handleSubmit, register, resetField, formState, watch} =
        useForm<PrependAttrFormValues>({
            defaultValues: formDefaults,
        });

    const [watchedDerivationFlag, watchedUsingReferenceValue] = watch([
        'isDerivation',
        'usingStaticReference',
    ]);

    // prevent filtering from running every render
    const filter = useCallback(
        (attr: BaseAttribute) => attr.type === 'category',
        []
    );

    const validatingRegister = useCallback(
        (inputName: keyof PrependAttrFormValues, options?: any) =>
            register(inputName, options ? options : {
                required: `${inputName} is required`,
            }),
        [register]
    );

    const queryRes = useAttrNeighbors(_id, filter);
    //
    // useEffect(() => {
    //     if (queryRes.isSuccess && closeModal) {
    //         closeModal();
    //     }
    // }, [queryRes.isSuccess, closeModal]);

    // useEffect(() => {
    //     if (queryRes.isSuccess && updatePopper) {
    //         updatePopper();
    //     }
    // }, [queryRes.isSuccess, updatePopper]);

    const [prependAttr, mutationResults] = useOperateAttrMutation();

    const mergedErrs = mergeErrorStates(queryRes, mutationResults);

    const onSubmit: SubmitHandler<PrependAttrFormValues> = (vals, e) => {
        e?.preventDefault();
        prependAttr({
            attrId: _id,
            body: prependAttrFormToPayload(vals),
        });
    };

    if (queryRes.isError) {
        return (
            <FlexContainer justifyContent="center">
                <Typography color="error" paragraph>
                    {extractQueryErrMessage(queryRes.error)}
                </Typography>
            </FlexContainer>
        );
    }

    return (
        <AppModal
            label={"Prepend Attribute"}
            isOpen={true}
            isDirty={formState.isDirty}
            onClose={closeModal}
            onSubmit={handleSubmit(onSubmit)}
            canEdit={canEdit || true}>
            <IsDerivationInput
                resetField={resetField}
                formState={formState}
                validatingRegister={validatingRegister}
                mutationResults={mutationResults}
            />
            {renderDerivationSubfields({
                isDerivation: watchedDerivationFlag === 'true',
                formState,
                validatingRegister,
                resetField,
                mutationResults
            })}
            <ValueTypeSelect
                resetField={resetField}
                formState={formState}
                validatingRegister={validatingRegister}
                mutationResults={mutationResults}
            />
            {!queryRes.isUninitialized && !queryRes.isLoading
                ? renderReferenceField(
                    'otherId',
                    'otherValue',
                    watchedUsingReferenceValue === 'true',
                    validatingRegister,
                    queryRes.attrNeighbors,
                    formState
                )
                : queryRes.isUninitialized
                    ? <Uninitialized/>
                    : <Loading/>}
            <DelimiterTypeSelect
                resetField={resetField}
                formState={formState}
                validatingRegister={validatingRegister}
                mutationResults={mutationResults}
            />
            <FormResults
                isError={!!mergedErrs}
                error={mergedErrs}
                validationErrors={formState.errors}
            />
        </AppModal>
    );
};

export default PrependAttributeForm;
